import React, { useEffect, useRef, useState } from 'react';
import { FiLoader, FiMapPin, FiSearch, FiX } from 'react-icons/fi';
import { searchPlaces } from '../../utils/locationSearchUtils';

function MapPlaceSearch({ onSelectPlace }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      setError('');
      setLoading(false);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const places = await searchPlaces(query);
        setResults(places);
        setOpen(true);
        if (!places.length) setError('No places found');
      } catch (err) {
        setResults([]);
        setError('Unable to search places right now');
      } finally {
        setLoading(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (place) => {
    onSelectPlace?.(place);
    setQuery(place.shortLabel || place.label);
    setOpen(false);
    setResults([]);
  };

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setError('');
    onSelectPlace?.(null);
  };

  return (
    <div className="map-place-search" ref={containerRef}>
      <div className="map-place-search__input-wrap">
        <FiSearch className="map-place-search__icon" />
        <input
          type="text"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Search place, city, address…"
          className="map-place-search__input"
        />
        {loading ? <FiLoader className="map-place-search__loader animate-spin" /> : null}
        {query ? (
          <button type="button" className="map-place-search__clear" onClick={clearSearch} aria-label="Clear search">
            <FiX />
          </button>
        ) : null}
      </div>

      {open && (results.length > 0 || error) ? (
        <div className="map-place-search__results">
          {error ? <p className="map-place-search__empty">{error}</p> : null}
          {results.map((place) => (
            <button
              key={place.id}
              type="button"
              className="map-place-search__result"
              onClick={() => handleSelect(place)}
            >
              <FiMapPin />
              <span>
                <strong>{place.shortLabel || place.label}</strong>
                {place.shortLabel && place.label !== place.shortLabel ? (
                  <small>{place.label}</small>
                ) : null}
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default MapPlaceSearch;
